import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { NavLink } from 'react-router-dom'; // useLocation removed as variable not used
import { useAuth } from '../context/AuthContext';
import { useDashboard } from '../context/DashboardContext';
import { motion } from 'framer-motion';
import {
  FiHome, FiPackage, FiFileText, FiShoppingCart,
  FiUser, FiCreditCard, FiDollarSign, FiList,
  FiHelpCircle, FiLogOut, FiMenu, FiX, FiBarChart2, FiLink,
  FiChevronDown, FiZap, FiFacebook
} from 'react-icons/fi';
// Removed: import axios from 'axios';

// --- Styled Components (unchanged, assuming they are optimized for their purpose) ---
const SidebarContainer = styled(motion.aside)`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  width: 260px;
  height: 100vh;
  position: fixed;
  right: 0;
  top: 0;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.08), -2px 0 8px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  overflow-x: hidden;
  border-left: 1px solid rgba(226, 232, 240, 0.8);
  backdrop-filter: blur(10px);

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(148, 163, 184, 0.3);
    border-radius: 3px;
    transition: background 0.2s ease;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(148, 163, 184, 0.5);
  }

  @media (max-width: 768px) {
    width: 85%;
    max-width: 280px;
  }

  @media (max-width: 480px) {
    width: 90%;
  }
`;

const SidebarHeader = styled.div`
  padding: 1.25rem 1rem 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(226, 232, 240, 0.6);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
`;

const Logo = styled.h1`
  background: linear-gradient(135deg, var(--primary-color) 0%, #667eea 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.5px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

const CloseButton = styled.button`
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid rgba(226, 232, 240, 0.6);
  border-radius: 10px;
  width: 36px;
  height: 36px;
  font-size: 1.1rem;
  color: var(--gray-600);
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);

  &:hover {
    background: rgba(239, 68, 68, 0.1);
    border-color: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }

  @media (max-width: 768px) {
    display: flex;
  }
`;

const NavList = styled.ul`
  list-style: none;
  padding: 0.75rem 0 1.5rem;
  margin: 0;
`;

const NavItem = styled.li`
  margin-bottom: 0.25rem;
`;

const NavItemLink = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 1rem;
  color: #475569;
  font-weight: 500;
  font-size: 0.9rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-right: 3px solid transparent;
  border-radius: 0 10px 10px 0;
  margin: 0 0.5rem 0 0;
  position: relative;
  text-decoration: none;

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%);
    color: #4f46e5;
    border-right-color: rgba(99, 102, 241, 0.3);
    transform: translateX(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
  }

  &.active {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.12) 0%, rgba(168, 85, 247, 0.12) 100%);
    color: #4f46e5;
    border-right-color: #4f46e5;
    font-weight: 600;
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);

    &::before {
      content: '';
      position: absolute;
      right: -0.5rem;
      top: 0;
      bottom: 0;
      width: 4px;
      background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
      border-radius: 2px 0 0 2px;
    }
  }

  &:active {
    transform: translateX(-1px) scale(0.98);
  }
`;

const SidebarFooter = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(226, 232, 240, 0.6);
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  margin-top: auto;
  position: sticky;
  bottom: 0;
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%);
  border: 1px solid rgba(99, 102, 241, 0.1);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(168, 85, 247, 0.08) 100%);
    border-color: rgba(99, 102, 241, 0.2);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.1);
  }
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1rem;
  margin-left: 0.625rem;
  box-shadow: 0 4px 12px rgba(79, 70, 229, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.9);
  transition: all 0.3s ease;

  ${UserInfo}:hover & {
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(79, 70, 229, 0.4);
  }
`;

const UserName = styled.p`
  font-weight: 600;
  font-size: 0.85rem;
  margin: 0 0 0.125rem 0;
  color: #1e293b;
`;

const UserPlan = styled.p`
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 500;
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${props => {
    if (props.type === 'plan') {
      return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
    }
    if (props.type === 'beta') {
      return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
    }
    return 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)';
  }};
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
`;

const NavItemContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;

  svg {
    margin-left: 0.625rem;
    font-size: 1.1rem;
    transition: all 0.2s ease;
    opacity: 0.8;
  }

  ${NavItemLink}:hover & svg {
    opacity: 1;
    transform: scale(1.1);
  }

  ${NavItemLink}.active & svg {
    opacity: 1;
  }
`;

const MobileToggle = styled.button`
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  color: white;
  border: none;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 1001;
  box-shadow: 0 8px 25px rgba(79, 70, 229, 0.3), 0 4px 10px rgba(0, 0, 0, 0.1);
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);

  &:hover {
    transform: scale(1.1) translateY(-2px);
    box-shadow: 0 12px 35px rgba(79, 70, 229, 0.4), 0 6px 15px rgba(0, 0, 0, 0.15);
  }

  &:active {
    transform: scale(1.05) translateY(-1px);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: rotate(180deg);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const NavGroup = styled.div`
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const NavGroupHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem 0.5rem;
  background: none;
  border: none;
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px;
  margin: 0 0.5rem;
  position: relative;

  &:hover {
    background: rgba(148, 163, 184, 0.08);
    color: #4f46e5;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    font-size: 0.9rem;
  }

  &[data-open="true"] svg {
    transform: rotate(180deg);
  }
`;

const NavGroupContent = styled.div`
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${props => props.$isOpen ? '500px' : '0'};
  opacity: ${props => props.$isOpen ? '1' : '0'};
  transform: translateY(${props => props.$isOpen ? '0' : '-10px'});
  padding-top: ${props => props.$isOpen ? '0.25rem' : '0'};
`;

const SubNavItem = styled.li`
  margin-bottom: 0.125rem;
  padding-right: 1rem;

  ${NavItemLink} {
    padding: 0.5rem 1rem 0.5rem 2rem;
    font-size: 0.85rem;
    font-weight: 500;
    margin-right: 0.75rem;
    border-radius: 0 8px 8px 0;
    position: relative;

    &:before {
      content: '';
      position: absolute;
      right: 1.75rem;
      top: 50%;
      transform: translateY(-50%);
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background-color: #cbd5e1;
      transition: all 0.2s ease;
    }

    &:hover:before {
      background-color: #4f46e5;
      transform: translateY(-50%) scale(1.2);
    }

    &.active {
      background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(168, 85, 247, 0.1) 100%);

      &:before {
        background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
        transform: translateY(-50%) scale(1.3);
        box-shadow: 0 0 8px rgba(79, 70, 229, 0.4);
      }

      &::after {
        content: '';
        position: absolute;
        right: 1.25rem;
        top: 50%;
        transform: translateY(-50%);
        width: 10px;
        height: 2px;
        background: linear-gradient(90deg, transparent 0%, #4f46e5 100%);
        border-radius: 1px;
      }
    }
  }
`;

const MoreButton = styled.button`
  background: transparent;
  border: none;
  color: #64748b;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.5rem 1.5rem 0.5rem 2rem;
  width: 100%;
  text-align: right;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &:hover {
    color: #4f46e5;
  }
`;
// --- End of Styled Components ---

// Helper function moved outside the component
const getInitials = (name) => {
  if (!name) return '';
  const names = name.split(' ');
  if (names.length >= 2) {
    return `${names[0][0]}${names[1][0]}`.toUpperCase();
  }
  if (names.length === 1 && names[0].length > 0) {
    return names[0][0].toUpperCase();
  }
  return '';
};

// تعريف مجموعات القائمة (grouped navigation structure)
const navGroups = [
  {
    id: 'dashboard',
    label: 'لوحة التحكم والإحصائيات',
    icon: <FiHome />,
    items: [
      { path: '/dashboard', label: 'لوحة التحكم', icon: <FiHome /> },
      { path: '/statistics', label: 'إحصائيات الطلبات', icon: <FiBarChart2 /> },
      { path: '/analytics', label: 'تحليلات الذكاء الاصطناعي', icon: <FiBarChart2 /> },
    ]
  },
  {
    id: 'products',
    label: 'المنتجات والصفحات',
    icon: <FiPackage />,
    items: [
      { path: '/products', label: 'المنتجات', icon: <FiPackage /> },
      { path: '/landing-pages', label: 'الصفحات', icon: <FiFileText /> },
    ]
  },
  {
    id: 'orders',
    label: 'إدارة الطلبات',
    icon: <FiShoppingCart />,
    items: [
      { path: '/orders', label: 'الطلبات', icon: <FiShoppingCart /> },
      { path: '/abandoned-orders', label: 'الطلبات المهجورة', icon: <FiShoppingCart /> },
    ]
  },
  {
    id: 'integrations',
    label: 'التكاملات',
    icon: <FiLink />,
    items: [
      { path: '/taager', label: 'ربط Taager', icon: <FiLink /> },
      { path: '/facebook', label: 'Facebook Conversion API', icon: <FiFacebook /> },
      { path: '/safka', label: 'ربط صفقة (Safka)', icon: <FiLink /> },
      { path: '/webhooks', label: 'إشعارات Webhook', icon: <FiZap /> },
    ]
  },
  {
    id: 'communication',
    label: 'التواصل والدعم',
    icon: <FiHelpCircle />,
    items: [
      { path: '/messages', label: 'الرسائل', icon: <FiFileText /> },
      { path: '/support', label: 'الدعم', icon: <FiHelpCircle /> },
    ]
  },
  {
    id: 'account',
    label: 'الحساب والرصيد',
    icon: <FiUser />,
    items: [
      { path: '/profile', label: 'الإعدادات الشخصية', icon: <FiUser /> },
      { path: '/plans', label: 'الخطط', icon: <FiCreditCard /> },
      { path: '/wallet/deposit', label: 'إضافة رصيد', icon: <FiDollarSign /> },
      { path: '/wallet/transactions', label: 'الفواتير / العمليات', icon: <FiList /> },
    ]
  },
];

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showAllIntegrations, setShowAllIntegrations] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    // Load from localStorage or use defaults
    const saved = localStorage.getItem('sidebar-groups-state');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.warn('Failed to parse saved sidebar state');
      }
    }
    return {
      dashboard: true,
      products: true,
      orders: true,
      integrations: false,
      communication: false,
      account: true,
    };
  });
  const { user, logout } = useAuth(); // Removed 'token'
  const { dashboardData } = useDashboard();

  // Initial counts state structure
  const [counts, setCounts] = useState({
    products: 0,
    landing_pages: 0,
    orders: 0,
    abandoned_orders: 0, // This specific field is not updated by dashboardData in original useEffect
    messages: 0,         // This specific field is not updated by dashboardData in original useEffect
    current_balance: 0,
    subscription_plan: { id: 'free', name: 'Free Plan' },
    total_abandoned_orders: 0,
    total_customer_messages: 0
  });

  // Memoized toggle and close functions
  const toggleSidebar = useCallback(() => {
    setIsOpen(prevIsOpen => !prevIsOpen);
  }, []);

  const closeSidebar = useCallback(() => {
    if (window.innerWidth <= 768) { // Check width at time of call
      setIsOpen(false);
    }
  }, []);

  const toggleGroup = useCallback((groupId) => {
    setOpenGroups(prev => {
      const newState = {
        ...prev,
        [groupId]: !prev[groupId]
      };
      // Save to localStorage
      localStorage.setItem('sidebar-groups-state', JSON.stringify(newState));
      return newState;
    });
  }, []);

  useEffect(() => {
    if (dashboardData) {
      setCounts(prev => ({
        ...prev, // Preserve all previous values first
        // Update with new values from dashboardData, fallback to previous if new is falsy
        products: dashboardData.products_count || prev.products,
        landing_pages: dashboardData.landing_pages_count || prev.landing_pages,
        orders: dashboardData.orders_count || prev.orders,
        current_balance: dashboardData.current_balance || prev.current_balance,
        subscription_plan: dashboardData.subscription_plan || prev.subscription_plan,
        total_abandoned_orders: dashboardData.total_abandoned_orders || prev.total_abandoned_orders,
        total_customer_messages: dashboardData.total_customer_messages || prev.total_customer_messages
        // Note: `abandoned_orders` and `messages` (non-total versions) from initial state are preserved
        // unless dashboardData explicitly provides fields to update them (which it doesn't seem to).
      }));
    }
  }, [dashboardData]); // Effect runs when dashboardData changes

  const handleLogout = useCallback(() => {
    logout();
    closeSidebar();
  }, [logout, closeSidebar]);

  return (
    <>
      <SidebarContainer
        $isOpen={isOpen} // Prop for styled-components if needed, though Framer Motion handles transform
        initial={{ x: typeof window !== 'undefined' && window.innerWidth <= 768 ? '100%' : 0 }}
        animate={{ x: (isOpen || (typeof window !== 'undefined' && window.innerWidth > 768)) ? 0 : '100%' }}
        transition={{ type: 'tween', duration: 0.3 }}
      >
        <SidebarHeader>
          <Logo>LiteSale</Logo>
          <CloseButton onClick={toggleSidebar}>
            <FiX />
          </CloseButton>
        </SidebarHeader>

        <NavList>
          {navGroups.map((group) => (
            <NavGroup key={group.id}>
              <NavGroupHeader
                onClick={() => toggleGroup(group.id)}
                data-open={openGroups[group.id]}
              >
                <NavItemContent>
                  {group.icon}
                  {group.label}
                </NavItemContent>
                <FiChevronDown />
              </NavGroupHeader>

              <NavGroupContent $isOpen={openGroups[group.id]}>
                {(group.id === 'integrations' && !showAllIntegrations ? group.items.slice(0, 2) : group.items).map((item) => (
                  <SubNavItem key={item.path}>
                    <NavItemLink
                      to={item.path}
                      onClick={closeSidebar}
                    >
                      <NavItemContent>
                        {item.icon}
                        {item.label}
                      </NavItemContent>

                      {/* Badge logic - unchanged, assumes `counts` state is correct */}
                      {item.path === '/products' && counts.products > 0 && (
                        <Badge>{counts.products}</Badge>
                      )}
                      {item.path === '/landing-pages' && counts.landing_pages > 0 && (
                        <Badge>{counts.landing_pages}</Badge>
                      )}
                      {item.path === '/orders' && counts.orders > 0 && (
                        <Badge>{counts.orders}</Badge>
                      )}
                      {item.path === '/abandoned-orders' && (counts.abandoned_orders > 0 || counts.total_abandoned_orders > 0) && (
                        <Badge>{counts.abandoned_orders || counts.total_abandoned_orders}</Badge>
                      )}
                      {item.path === '/messages' && (counts.messages > 0 || counts.total_customer_messages > 0) && (
                        <Badge>{counts.messages || counts.total_customer_messages}</Badge>
                      )}
                      {item.path === '/plans' && counts.subscription_plan?.id && (
                        <Badge type="plan">{counts.subscription_plan.id}</Badge>
                      )}
                      {item.path === '/wallet/deposit' && typeof counts.current_balance === 'number' && (
                        <Badge>£{counts.current_balance.toFixed(2)}</Badge>
                      )}
                      {item.path === '/safka' && (
                        <Badge type="beta">BETA</Badge>
                      )}
                    </NavItemLink>
                  </SubNavItem>
                ))}
                {group.id === 'integrations' && group.items.length > 2 && (
                  <SubNavItem>
                    <MoreButton onClick={() => setShowAllIntegrations(prev => !prev)}>
                      {showAllIntegrations ? 'عرض أقل' : 'عرض المزيد...'}
                    </MoreButton>
                  </SubNavItem>
                )}
              </NavGroupContent>
            </NavGroup>
          ))}

          <div style={{
            margin: '1rem 0.5rem 0.75rem',
            height: '1px',
            background: 'linear-gradient(90deg, transparent 0%, rgba(226, 232, 240, 0.6) 50%, transparent 100%)'
          }} />

          <NavItem>
            <NavItemLink
              as="button"
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)',
                color: '#dc2626',
                fontWeight: '600',
                border: '1px solid rgba(239, 68, 68, 0.2)',
                borderRadius: '0 12px 12px 0',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(220, 38, 38, 0.12) 100%)';
                e.target.style.transform = 'translateX(-2px)';
                e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.08) 100%)';
                e.target.style.transform = 'translateX(0)';
                e.target.style.boxShadow = 'none';
              }}
            >
              <NavItemContent>
                <FiLogOut />
                تسجيل الخروج
              </NavItemContent>
            </NavItemLink>
          </NavItem>
        </NavList>

        {user && (
          <SidebarFooter>
            <UserInfo>
              <UserAvatar>{getInitials(user.fullName)}</UserAvatar>
              <div>
                <UserName>{user.fullName}</UserName>
                <UserPlan>{counts.subscription_plan?.name || 'Free Plan'}</UserPlan>
              </div>
            </UserInfo>
          </SidebarFooter>
        )}
      </SidebarContainer>

      <MobileToggle onClick={toggleSidebar}>
        {isOpen ? <FiX /> : <FiMenu />}
      </MobileToggle>
    </>
  );
};

export default React.memo(Sidebar); // Memoizing Sidebar if its props (none in this case) are stable